import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M11_ROLES, hasAnyRole } from '../../lib/roles';
import type { Award, Contract, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function AwardDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const isBoard = hasAnyRole(persona?.roles, M11_ROLES.TENDER_BOARD);
  const isOfficer = hasAnyRole(persona?.roles, M11_ROLES.PROCUREMENT_OFFICER);

  const awardQ = useApiQuery<Award>(`/api/v1/m11/awards/${id}`);
  const contractsQ = useApiQuery<Page<Contract>>('/api/v1/m11/contracts?limit=100');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (awardQ.loading) return <Spinner label="Loading award…" />;
  if (awardQ.error || !awardQ.data) {
    return (
      <div>
        <Link to="/procurement/awards" className="text-sm font-semibold text-brand-600 hover:underline">← Back to awards</Link>
        <div className="mt-4"><ErrorNote message={awardQ.error ?? 'Award not found'} /></div>
      </div>
    );
  }
  const award = awardQ.data;
  const contract = contractsQ.data?.items.find((c) => c.awardId === award.id);

  const act = async (action: 'confirm' | 'finalize' | 'contract') => {
    setBusy(action);
    setError(null);
    try {
      const path = action === 'contract' ? `/api/v1/m11/awards/${award.id}/contract` : `/api/v1/m11/awards/${award.id}/${action}`;
      await api.post(path);
      awardQ.reload();
      contractsQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <Link to="/procurement/awards" className="mb-3 inline-block text-sm font-semibold text-brand-600 hover:underline">← Back to awards register</Link>
      <PageHeader title={`Award ${award.refNo}`} subtitle={`Tender ${award.tenderId}`} actions={<StatusBadge status={award.status} />} />

      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <div className="card px-4 py-3"><div className="text-xs font-semibold uppercase text-slate-500">Amount</div><div className="mt-0.5 text-sm font-bold tabular-nums">{scr(award.amountScr)}</div></div>
        <div className="card px-4 py-3"><div className="text-xs font-semibold uppercase text-slate-500">Supplier</div><div className="mt-0.5 font-mono text-xs">{award.supplierId}</div></div>
        <div className="card px-4 py-3"><div className="text-xs font-semibold uppercase text-slate-500">Score</div><div className="mt-0.5 text-sm font-bold tabular-nums">{award.totalScore ?? '—'}</div></div>
      </div>

      <div className="card mb-5 p-5">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Timeline</h2>
        <dl className="space-y-1 text-sm">
          <div className="flex justify-between"><dt className="text-slate-500">Proposed</dt><dd>{formatDateTime(award.proposedAt)} by {award.proposedBy}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Confirmed</dt><dd>{formatDateTime(award.confirmedAt)}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Finalized</dt><dd>{formatDateTime(award.finalizedAt)}</dd></div>
          {award.reversedAt ? <div className="flex justify-between"><dt className="text-slate-500">Reversed</dt><dd>{formatDateTime(award.reversedAt)} — {award.reversedReason}</dd></div> : null}
        </dl>
        {error ? <div className="mt-3"><ErrorNote message={error} /></div> : null}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {isBoard && award.status === 'PROPOSED' ? <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => act('confirm')} disabled={busy !== null}>Confirm</button> : null}
          {(isBoard || isOfficer) && award.status === 'CONFIRMED' ? <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => act('finalize')} disabled={busy !== null}>Finalize</button> : null}
          {isOfficer && award.status === 'FINAL' && !contract ? <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => act('contract')} disabled={busy !== null}>Sign contract</button> : null}
          {contract ? <Link to={`/procurement/contracts/${contract.id}`} className="btn-secondary !px-2.5 !py-1 text-xs">View contract {contract.refNo} ↗</Link> : null}
        </div>
      </div>

      <AppealLodge award={award} />
    </div>
  );
}

function AppealLodge({ award }: { award: Award }) {
  const { persona } = useAuth();
  const [grounds, setGrounds] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!persona?.actingForBusinessId) {
    return <InfoNote message="Sign in as a business representative who bid on this tender to lodge an appeal during standstill." />;
  }

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m11/awards/${award.id}/appeals`, { grounds }, { actingFor: persona.actingForBusinessId });
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not lodge the appeal');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Lodge an appeal</h2>
      <p className="mb-3 text-xs text-slate-500">Only open while the tender is in STANDSTILL, and only to a losing bidder on this lot. Lodging freezes the standstill clock.</p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      {done ? (
        <InfoNote message="Appeal lodged — see Procurement › Appeals for status." />
      ) : (
        <div className="space-y-2">
          <Field label="Grounds"><textarea className="field-input" rows={3} value={grounds} onChange={(e) => setGrounds(e.target.value)} /></Field>
          <button className="btn-danger" onClick={submit} disabled={busy || grounds.length < 10}>{busy ? 'Lodging…' : 'Lodge appeal'}</button>
        </div>
      )}
    </div>
  );
}
